import { cn } from "@/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

/**
 * Aviso contextual dentro da tela (não é toast).
 *
 * Cada tom traz um ícone próprio além da cor: em WCAG, cor não pode ser o único
 * meio de transmitir informação (1.4.1). O ícone é `aria-hidden` porque o
 * conteúdo textual já diz tudo — ele existe para quem enxerga, não para quem
 * ouve a página.
 *
 * Entrada e saída são animadas porque um aviso que aparece ou some de repente
 * num formulário passa despercebido — ou, pior, empurra o conteúdo e faz o
 * usuário clicar no lugar errado. A altura é animada junto com a opacidade
 * justamente para o resto da tela acomodar em vez de pular.
 */

type AlertTone = "info" | "success" | "warning" | "danger";

const toneConfig: Record<
  AlertTone,
  {
    icon: ComponentType<{ className?: string }>;
    className: string;
    iconClass: string;
  }
> = {
  info: {
    icon: Info,
    className: "border-ds-secondary-200 bg-ds-info-50 text-ds-secondary-900",
    iconClass: "text-ds-secondary-700",
  },
  success: {
    icon: CheckCircle2,
    className: "border-ds-success-200 bg-ds-success-50 text-ds-ink-900",
    iconClass: "text-ds-success-700",
  },
  warning: {
    icon: AlertTriangle,
    className: "border-ds-warning-200 bg-ds-warning-50 text-ds-ink-900",
    iconClass: "text-ds-warning-700",
  },
  danger: {
    icon: XCircle,
    className: "border-ds-danger-200 bg-ds-danger-50 text-ds-ink-900",
    iconClass: "text-ds-danger-700",
  },
};

/** Curvas e durações do DESIGN.md: 240ms para entrar, 180ms para sair. */
const EASE_OUT = [0.16, 1, 0.3, 1] as const;
const EASE_IN = [0.7, 0, 0.84, 0] as const;

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  /** Ação de saída do problema ("Atualizar dados"). Opcional. */
  action?: ReactNode;
  /**
   * Controla a presença do aviso com animação de entrada e de saída.
   * Omitido, o aviso simplesmente está lá — que é o caso da maioria das telas.
   */
  open?: boolean;
  /** Passando esta função, aparece o botão de fechar. */
  onDismiss?: () => void;
  /** Rótulo do botão de fechar para leitores de tela. */
  dismissLabel?: string;
  className?: string;
}

export const Alert = ({
  tone = "info",
  title,
  children,
  action,
  open = true,
  onDismiss,
  dismissLabel = "Fechar aviso",
  className,
}: AlertProps) => {
  const { icon: Icon, className: toneClass, iconClass } = toneConfig[tone];
  // Quem pediu menos movimento recebe a troca instantânea, não uma versão lenta.
  const semMovimento = useReducedMotion();
  const duracao = (ms: number) => (semMovimento ? 0 : ms / 1000);

  return (
    // initial={false} para que um aviso já presente no carregamento da tela não
    // entre animado — a animação é para a mudança, não para a chegada.
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="alert"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            height: { duration: duracao(240), ease: EASE_OUT },
            opacity: { duration: duracao(180), ease: EASE_OUT },
          }}
          className="overflow-hidden"
        >
          <motion.div
            initial={{ y: -8 }}
            animate={{ y: 0 }}
            exit={{
              y: -8,
              transition: { duration: duracao(180), ease: EASE_IN },
            }}
            transition={{ duration: duracao(240), ease: EASE_OUT }}
            role={tone === "danger" ? "alert" : "status"}
            className={cn(
              "rounded-ds-md flex flex-col gap-3 border p-3 sm:flex-row sm:items-start sm:p-4",
              toneClass,
              className
            )}
          >
            <Icon
              aria-hidden="true"
              className={cn("size-5 shrink-0", iconClass)}
            />

            <div className="flex flex-1 flex-col gap-1">
              {title ? (
                <p className="font-ds-sans text-ds-body font-semibold">
                  {title}
                </p>
              ) : null}
              <div className="font-ds-sans text-ds-small [&_a]:underline [&_a]:underline-offset-2">
                {children}
              </div>
            </div>

            {action ? <div className="shrink-0 sm:ml-2">{action}</div> : null}

            {onDismiss ? (
              <button
                type="button"
                onClick={onDismiss}
                aria-label={dismissLabel}
                className={cn(
                  "rounded-ds-sm -m-1 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center self-start",
                  "ease-ds-out hover:bg-ds-ink-900/8 transition-colors duration-150",
                  "focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:outline-none",
                  "motion-reduce:transition-none"
                )}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
